import React, { useEffect, useState } from 'react';
import { FieldPath, UseFormReturn } from 'react-hook-form';

import { AnimatePresence, motion } from 'framer-motion';
import {
  CheckIcon,
  MessageSquare,
  PencilIcon,
  TrashIcon,
  XIcon,
} from 'lucide-react';

import { ActionButton } from 'components/ActionButton';
import { Label } from 'components/Label';
import { Select } from 'components/Select';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { Maybe } from 'types/UtilityTypes';

import { QuickQuoteInputs, ThreeGCommentItem } from '../../types';
import { CARD_ANIMATIONS as ANIMATIONS } from './cardAnimations';

const FIELD_PATH = 'ltl.comments';

interface CommentCardProps {
  index: number;
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
  onRemove: () => void;
  commentTypeOptions: Array<{ value: string; label: string }>;
  isLoadingCommentTypes: boolean;
}

export function CommentCard({
  index,
  formMethods,
  onRemove,
  commentTypeOptions,
  isLoadingCommentTypes,
}: CommentCardProps) {
  const { watch, setValue } = formMethods;
  const [isEditing, setIsEditing] = useState(false);
  const [originalValues, setOriginalValues] =
    useState<Maybe<Partial<ThreeGCommentItem>>>(null);

  const watchedComment = watch(`${FIELD_PATH}.${index}`);
  const isEmptyComment =
    !watchedComment?.commentType &&
    !watchedComment?.comment &&
    (watchedComment?.stopNumeric == null ||
      !Number.isFinite(watchedComment.stopNumeric));

  const handlers = {
    edit: () => {
      setOriginalValues({ ...watchedComment });
      setIsEditing(true);
    },
    save: () => {
      setIsEditing(false);
      setOriginalValues(null);
    },
    discard: () => {
      if (originalValues) {
        const id = watchedComment?.id ?? originalValues.id ?? '';
        const fullSnapshot: ThreeGCommentItem = {
          id,
          commentType: originalValues.commentType,
          commentTypeLabel: originalValues.commentTypeLabel,
          stopNumeric: originalValues.stopNumeric,
          comment: originalValues.comment,
        };
        setValue(
          `${FIELD_PATH}.${index}` as FieldPath<QuickQuoteInputs>,
          fullSnapshot,
          { shouldValidate: true }
        );
        setIsEditing(false);
        setOriginalValues(null);
      } else {
        onRemove();
      }
    },
    remove: onRemove,
  };

  useEffect(() => {
    if (isEmptyComment && !isEditing) {
      setIsEditing(true);
    }
  }, [isEmptyComment, isEditing]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && isEditing) {
      e.preventDefault();
      e.stopPropagation();
      handlers.save();
    }
  };

  const renderActions = () => (
    <AnimatePresence mode='wait'>
      {isEditing ? (
        <motion.div
          key='editing-actions'
          {...ANIMATIONS.actions}
          className='flex gap-1.5'
        >
          {!isEmptyComment ? (
            <>
              <ActionButton
                onClick={handlers.save}
                className='hover:bg-info-200 text-info-600'
                icon={CheckIcon}
                aria-label='Save changes'
              />
              <ActionButton
                onClick={handlers.discard}
                className='hover:bg-error-200 text-error-600'
                icon={XIcon}
                aria-label='Discard changes'
              />
            </>
          ) : (
            <ActionButton
              onClick={handlers.remove}
              className='hover:bg-error-300 text-error-600'
              icon={TrashIcon}
              aria-label='Remove comment'
            />
          )}
        </motion.div>
      ) : (
        <motion.div
          key='view-actions'
          {...ANIMATIONS.actions}
          className='flex gap-1.5'
        >
          <ActionButton
            onClick={handlers.edit}
            className='hover:bg-info-300 text-info-600'
            icon={PencilIcon}
            aria-label='Edit comment'
          />
          <ActionButton
            onClick={handlers.remove}
            className='hover:bg-error-400 text-error-600'
            icon={TrashIcon}
            aria-label='Remove comment'
          />
        </motion.div>
      )}
    </AnimatePresence>
  );

  const summaryParts: string[] = [];

  if (watchedComment?.commentTypeLabel) {
    summaryParts.push(watchedComment.commentTypeLabel);
  }

  if (
    watchedComment?.stopNumeric != null &&
    Number.isFinite(watchedComment.stopNumeric)
  ) {
    summaryParts.push(`Stop ${watchedComment.stopNumeric}`);
  }

  if (watchedComment?.comment) {
    summaryParts.push(watchedComment.comment);
  }

  const summaryLabel =
    summaryParts.length > 0 ? summaryParts.join(' • ') : `Comment ${index + 1}`;

  const renderViewMode = () => (
    <motion.div
      key='view-mode'
      {...ANIMATIONS.toggle}
      className='overflow-hidden cursor-pointer'
      onClick={handlers.edit}
      onKeyDown={(e: React.KeyboardEvent) =>
        e.key === 'Enter' && handlers.edit()
      }
      role='button'
      tabIndex={0}
      aria-label='Click to edit comment'
    >
      <Flex justify='between' align='center' className='min-w-0'>
        <Flex align='center' gap='sm' className='flex-1 min-w-0 mr-2'>
          <MessageSquare className='h-4 w-4 text-neutral-500 shrink-0' />
          <Typography
            variant='body-sm'
            weight='medium'
            className='truncate'
            title={summaryLabel}
          >
            {summaryLabel}
          </Typography>
        </Flex>
        <div onClick={(e) => e.stopPropagation()} className='shrink-0'>
          {renderActions()}
        </div>
      </Flex>
    </motion.div>
  );

  const renderEditingForm = () => (
    <motion.div
      key='editing-form'
      {...ANIMATIONS.toggle}
      className='overflow-hidden'
    >
      <Flex justify='between' align='center' className='mb-3'>
        <Flex align='center' gap='sm'>
          <Typography variant='body-sm' weight='medium'>
            Edit Comment
          </Typography>
        </Flex>
        <div onClick={(e) => e.stopPropagation()}>{renderActions()}</div>
      </Flex>

      <Grid cols='2' gap='lg' className='w-full mx-0'>
        <div className='w-full'>
          <Label name={`${FIELD_PATH}.${index}.commentType`}>
            Comment Type
          </Label>
          <Select
            value={watch(`${FIELD_PATH}.${index}.commentType`) ?? ''}
            onChange={(value) => {
              const strValue = value != null ? String(value) : '';
              setValue(`${FIELD_PATH}.${index}.commentType`, strValue);
              const selected = commentTypeOptions.find(
                (opt) => String(opt.value) === strValue
              );
              setValue(
                `${FIELD_PATH}.${index}.commentTypeLabel`,
                selected?.label ?? ''
              );
            }}
            options={commentTypeOptions}
            showSearch
            placeholder={
              isLoadingCommentTypes
                ? 'Loading...'
                : 'Select or search comment type'
            }
            disabled={isLoadingCommentTypes}
            className='w-full mt-1'
          />
        </div>

        <div className='w-full'>
          <RHFTextInput
            name={
              `${FIELD_PATH}.${index}.stopNumeric` as FieldPath<QuickQuoteInputs>
            }
            label='Stop numeric'
            inputType='number'
            placeholder='0'
            options={{ valueAsNumber: true, min: 0 }}
            required={false}
          />
        </div>

        <div className='w-full' style={{ gridColumn: '1 / -1' }}>
          <RHFTextInput
            name={
              `${FIELD_PATH}.${index}.comment` as FieldPath<QuickQuoteInputs>
            }
            label='Comment'
            placeholder='Enter comment or special instructions'
            required={false}
          />
        </div>
      </Grid>
    </motion.div>
  );

  return (
    <motion.div
      layout
      transition={{ type: 'spring', duration: 0.2, bounce: 0 }}
      {...ANIMATIONS.card}
      className='w-full overflow-hidden rounded-md border border-neutral-400 shadow-sm relative p-3 mb-2'
      onKeyDown={handleKeyDown}
    >
      <AnimatePresence mode='wait'>
        {isEditing ? renderEditingForm() : renderViewMode()}
      </AnimatePresence>
    </motion.div>
  );
}
